import * as React from "react";
import { createPortal } from "react-dom";
import { MoreHorizontal } from "lucide-react";
import { cn } from "../../lib/utils";

/**
 * OverflowMenu — the shared "⋯" actions menu. Destructive actions live here
 * behind a confirm step (never as a one-click primary-weight control). Fully
 * keyboard-navigable (Enter/Space/ArrowDown to open, arrows to move, Escape
 * to close) and restores focus to the trigger on close.
 *
 * <p>🔴 <b>The panel is PORTALLED to `document.body` and positioned `fixed`
 * against the trigger's rect, and that is not a style choice.</b> It used to be
 * an `absolute` child of an `inline-block` wrapper, which meant any ancestor
 * establishing a clipping context cut it off — and `Table` sets
 * `overflow-x-auto` UNCONDITIONALLY on every table in the app. Per CSS, an
 * `overflow-x` other than `visible` forces `overflow-y` from `visible` to
 * `auto`, so the wrapper clips and scrolls in BOTH axes: the menu was cut off at
 * the table's edge, scrolled with the table rather than the page, and its own
 * height made a one-row table sprout a vertical scrollbar the moment it opened.
 * `z-index` cannot escape a clipping ancestor — only leaving the subtree can.</p>
 *
 * <p>Eleven surfaces were affected and none had been reported, because a
 * clipped menu still opens: it just opens somewhere you cannot fully see.</p>
 *
 * <p>Positioning follows {@link Tooltip}'s in-repo pattern rather than adding a
 * floating-ui dependency — measure before paint, flip when the preferred side
 * does not fit, clamp to the viewport, and track `scroll` (capture phase, so
 * an inner scroller counts) and `resize`.</p>
 */
export interface OverflowMenuItem {
  label: string;
  onSelect: () => void;
  /** Renders in danger tone and, unless `confirm` is set, still safe to click. */
  destructive?: boolean;
  /** Confirmation prompt shown inline before the action fires. */
  confirm?: string;
  disabled?: boolean;
  /** A short line under the item — typically WHY a disabled item is disabled.
   *  Rendered outside the dimmed button so it stays readable, and wired to it
   *  with `aria-describedby`. */
  hint?: string;
}

export interface OverflowMenuProps {
  items: OverflowMenuItem[];
  /** Accessible label for the trigger. */
  label?: string;
  className?: string;
  "data-testid"?: string;
}

export function OverflowMenu({
  items,
  label = "More actions",
  className,
  "data-testid": testId,
}: OverflowMenuProps) {
  const [open, setOpen] = React.useState(false);
  const menuId = React.useId();
  const [confirming, setConfirming] = React.useState<number | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const itemRefs = React.useRef<(HTMLButtonElement | null)[]>([]);
  /** Null until measured. The panel renders `invisible` for that one frame so
   *  it still has a box to measure but never paints in the wrong place. */
  const [pos, setPos] = React.useState<{ top: number; left: number } | null>(null);

  const close = React.useCallback((restoreFocus = true) => {
    setOpen(false);
    setConfirming(null);
    setPos(null);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  const reposition = React.useCallback(() => {
    const trigger = triggerRef.current;
    const menu = menuRef.current;
    if (!trigger || !menu) return;

    const rect = trigger.getBoundingClientRect();
    const { offsetWidth: width, offsetHeight: height } = menu;
    const gap = 4;

    // Below by default; flip above only when below does not fit AND above does,
    // so a menu taller than either side still opens downward rather than off the
    // top of the screen.
    const fitsBelow = rect.bottom + gap + height <= window.innerHeight;
    const fitsAbove = rect.top - gap - height >= 0;
    const top = !fitsBelow && fitsAbove ? rect.top - gap - height : rect.bottom + gap;

    // Right-aligned to the trigger, which is what `right-0` did while the panel
    // was a child of it. Clamped so a trigger near either edge still yields a
    // fully visible panel.
    const left = Math.max(
      gap,
      Math.min(rect.right - width, window.innerWidth - width - gap),
    );

    setPos((prev) =>
      prev && prev.top === top && prev.left === left ? prev : { top, left },
    );
  }, []);

  // Measured before paint, so the first frame is already in the right place.
  React.useLayoutEffect(() => {
    if (!open) return;
    reposition();
    const onViewportChange = () => reposition();
    // Capture phase: the trigger frequently sits inside its own scroller (a
    // table wrapper), and a scroll there does not bubble to `window`.
    window.addEventListener("scroll", onViewportChange, true);
    window.addEventListener("resize", onViewportChange);
    return () => {
      window.removeEventListener("scroll", onViewportChange, true);
      window.removeEventListener("resize", onViewportChange);
    };
  }, [open, reposition]);

  // The confirm step grows the panel, so it has to be re-measured or a menu that
  // flipped upward would extend off the top.
  React.useLayoutEffect(() => {
    if (open) reposition();
  }, [confirming, open, reposition]);

  // Close on outside click.
  React.useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        !triggerRef.current?.contains(e.target as Node)
      ) {
        close(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open, close]);

  // Focus the first item when opening.
  React.useEffect(() => {
    if (open) itemRefs.current[0]?.focus();
  }, [open]);

  function moveFocus(delta: number) {
    const focusable = itemRefs.current.filter(Boolean) as HTMLButtonElement[];
    if (focusable.length === 0) return;
    const active = document.activeElement as HTMLElement | null;
    const idx = focusable.findIndex((el) => el === active);
    const next = (idx + delta + focusable.length) % focusable.length;
    focusable[next]?.focus();
  }

  function onMenuKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      e.stopPropagation();
      close();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      moveFocus(1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      moveFocus(-1);
    }
  }

  function activate(item: OverflowMenuItem, index: number) {
    if (item.disabled) return;
    if (item.confirm && confirming !== index) {
      setConfirming(index);
      return;
    }
    close();
    item.onSelect();
  }

  return (
    <div className={cn("relative inline-block", className)} data-testid={testId}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        onClick={() => (open ? close() : setOpen(true))}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" && !open) {
            e.preventDefault();
            setOpen(true);
          }
        }}
        className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border-light bg-transparent text-onyx hover:bg-white-warm focus-visible:outline-none focus-visible:shadow-goldRing"
      >
        <MoreHorizontal size={18} aria-hidden />
      </button>
      {open &&
        createPortal(
        <div
          ref={menuRef}
          role="menu"
          aria-label={label}
          onKeyDown={onMenuKeyDown}
          style={{ top: pos?.top ?? 0, left: pos?.left ?? 0 }}
          // `fixed`, and above the modal layer (z-50) so a row menu inside a
          // dialog still paints over it — but below the toast layer (z-60),
          // which must stay readable over everything. `invisible` (not
          // `hidden`) for the pre-measure frame, so it still has a box.
          // The slide-in animation is gone: it animated from the right edge of
          // a wrapper this panel no longer lives in.
          className={cn(
            "fixed z-[55] min-w-[200px] overflow-hidden rounded-md border border-border-light bg-white-pure py-space-1 shadow-lg",
            !pos && "invisible",
          )}
        >
          {items.map((item, index) => {
            const isConfirming = confirming === index;
            return (
              <div key={index} role="none">
                <button
                  ref={(el) => {
                    itemRefs.current[index] = el;
                  }}
                  role="menuitem"
                  type="button"
                  disabled={item.disabled}
                  aria-describedby={item.hint ? `${menuId}-hint-${index}` : undefined}
                  onClick={() => activate(item, index)}
                  className={cn(
                    "flex w-full items-center px-space-3 py-space-2 text-left text-small transition-colors duration-fast ease-standard",
                    "focus-visible:outline-none focus-visible:bg-white-warm hover:bg-white-warm",
                    "disabled:opacity-40 disabled:pointer-events-none",
                    item.destructive ? "text-status-error" : "text-onyx",
                  )}
                >
                  {isConfirming ? (item.confirm as string) : item.label}
                </button>
                {item.hint && (
                  <p
                    id={`${menuId}-hint-${index}`}
                    className="m-0 px-space-3 pb-space-2 -mt-space-1 text-caption text-gray-mid"
                  >
                    {item.hint}
                  </p>
                )}
                {isConfirming && (
                  <div className="flex items-center gap-space-2 px-space-3 pb-space-2">
                    <button
                      role="menuitem"
                      type="button"
                      onClick={() => {
                        close();
                        item.onSelect();
                      }}
                      className="rounded-sm bg-status-error px-space-2 py-[2px] text-label font-bold uppercase tracking-button text-white-pure hover:opacity-90 focus-visible:outline-none focus-visible:shadow-goldRing"
                    >
                      Confirm
                    </button>
                    <button
                      role="menuitem"
                      type="button"
                      onClick={() => setConfirming(null)}
                      className="text-label font-semibold uppercase tracking-button text-gray-mid hover:text-onyx focus-visible:outline-none focus-visible:shadow-goldRing rounded-sm"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
}
